import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_LINKS } from "../../data/config";
import { SocialIcons } from "../common/SocialIcons";

const LANGS = [
  { code: "ms", label: "BM" },
  { code: "en", label: "EN" },
  { code: "zh", label: "中文" },
  { code: "ta", label: "தமிழ்" },
];

const openAira = () => window.dispatchEvent(new CustomEvent("aira:open"));

const Wordmark = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center">
    <img src="/images/logo.png" alt="Irma Ariandi — iINSPIRE" className="h-11 w-auto" />
  </Link>
);

const LangSelector = ({ onPick }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label="Select language"
        aria-expanded={open}
        data-testid="nav-language-selector"
        className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-ink/70 transition-colors hover:text-burgundy"
      >
        EN <ChevronDown className="h-3 w-3" strokeWidth={1.5} />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-28 border border-ink/10 bg-white py-1 shadow-lg">
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => {
                setOpen(false);
                if (onPick) onPick();
                navigate(`/dental/${l.code}`);
              }}
              data-testid={`nav-lang-${l.code}`}
              className="block w-full px-4 py-2 text-left text-xs text-ink/70 hover:bg-softgrey hover:text-ink"
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-ink/10 bg-warmwhite/90 backdrop-blur-xl">
      <div className="container-site flex h-20 items-center justify-between gap-4">
        <Wordmark />
        <nav className="hidden items-center gap-6 xl:flex" aria-label="Main navigation">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.path === "/"}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors duration-300 ${
                  isActive ? "text-burgundy underline decoration-amber decoration-2 underline-offset-8" : "text-ink/75 hover:text-burgundy"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-5 xl:flex">
          <LangSelector />
          <button
            type="button"
            onClick={openAira}
            data-testid="nav-talk-to-aira"
            className="flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-[11px] font-semibold text-warmwhite transition-colors duration-300 hover:bg-burgundy"
          >
            <span className="h-2 w-2 rounded-full bg-[#FF3B8D]" aria-hidden="true" />
            Talk to Aira
          </button>
          <SocialIcons className="hidden 2xl:flex" />
        </div>
        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="text-ink xl:hidden"
        >
          {open ? <X className="h-6 w-6" strokeWidth={1.5} /> : <Menu className="h-6 w-6" strokeWidth={1.5} />}
        </button>
      </div>
      {open && (
        <div className="border-t border-ink/10 bg-warmwhite xl:hidden" data-testid="mobile-menu">
          <nav className="container-site flex flex-col gap-1 py-6" aria-label="Mobile navigation">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                end={link.path === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `py-3 text-xs font-semibold uppercase tracking-[0.2em] ${isActive ? "text-burgundy" : "text-ink/80"}`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <div className="mt-4 flex items-center gap-5 border-t border-ink/10 pt-5">
              <LangSelector onPick={() => setOpen(false)} />
              <button
                type="button"
                onClick={() => { setOpen(false); openAira(); }}
                data-testid="mobile-talk-to-aira"
                className="flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-[11px] font-semibold text-warmwhite"
              >
                <span className="h-2 w-2 rounded-full bg-[#FF3B8D]" aria-hidden="true" />
                Talk to Aira
              </button>
            </div>
            <div className="mt-5 text-ink">
              <SocialIcons />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
